import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { TranslateService } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';
import { LOCALE_ID, Type } from '@angular/core';
import { ConfigService } from '../service/config-service';
import { APP_CONFIG_PATH, APP_VERSION_PATH, storageKey } from '../../app.const';
import { makeUrl } from '../function/custom-function';

export interface BootstrapConfig {
  appModule: Type<any>;
  environment: any;
  locales?: { data: any; id: string; extra?: any }[];
  configRefreshInterval?: number;
  providers?: any[];
}

interface StoredConfig {
  timestamp: number;
  data: any;
}

export class AppBootstrap {
  static async bootstrap(config: BootstrapConfig): Promise<void> {
    if (config.locales) {
      config.locales.forEach((locale) => {
        if (locale.extra) {
          registerLocaleData(locale.data, locale.id, locale.extra);
        } else {
          registerLocaleData(locale.data, locale.id);
        }
      });
    }

    try {
      const appConfig = await this.loadConfig(config.environment, config.configRefreshInterval || 0);
      this.startApp(appConfig, config.appModule, config.environment, config.providers);
    } catch (error) {
      console.error('Failed to load configuration:', error);
    }
  }

  private static async loadConfig(environment: any, refreshInterval: number): Promise<any> {
    const storedConfigString = sessionStorage.getItem(storageKey.appConfig);

    try {
      if (storedConfigString) {
        const storedConfig: StoredConfig = JSON.parse(storedConfigString);
        if (!this.shouldRefetchConfig(storedConfig, refreshInterval)) {
          return storedConfig.data;
        }
      }
    } catch (error) {
      console.error('Error parsing stored config:', error);
    }

    this.fetchAndStoreVersion(environment).catch((err) => {
      console.error('Failed to fetch app version:', err);
    });

    return this.fetchAndStoreConfig(environment);
  }

  private static async fetchAndStoreConfig(environment: any): Promise<any> {
    const configUrl = this.getUrlWithTimeSignature(makeUrl(environment, APP_CONFIG_PATH));
    const response = await fetch(configUrl);
    const config = await response.json();

    const storedConfig: StoredConfig = {
      timestamp: Date.now(),
      data: config
    };

    sessionStorage.setItem(storageKey.appConfig, JSON.stringify(storedConfig));
    return config;
  }

  private static async fetchAndStoreVersion(environment: any): Promise<any> {
    const versionUrl = this.getUrlWithTimeSignature(makeUrl(environment, APP_VERSION_PATH));
    const response = await fetch(versionUrl);
    if (response.ok) {
      const result = await response.json();
      sessionStorage.setItem(storageKey.appVersion, result.version);
    }
  }

  private static getUrlWithTimeSignature(url: any): string {
    const timestamp = new Date().setHours(0, 0, 0, 0);
    return `${url}?t=${timestamp}`;
  }

  private static shouldRefetchConfig(storedConfig: StoredConfig, refreshInterval: number): boolean {
    if (!storedConfig.timestamp) {
      return true;
    }

    const now = Date.now();
    return now - storedConfig.timestamp > refreshInterval;
  }

  private static startApp(config: any, appModule: Type<any>, environment: any, additionalProviders: any[] = []): void {
    ConfigService.setEnvironment(config, environment);

    platformBrowserDynamic()
      .bootstrapModule(appModule, {
        providers: [
          ...additionalProviders,
          {
            provide: LOCALE_ID,
            useFactory: (translate: TranslateService) => this.getCulture(translate.currentLang, environment),
            deps: [TranslateService]
          }
        ]
      })
      .catch((err) => console.error('Bootstrap error:', err));
  }

  private static getCulture(locale: string, environment: any): string {
    const item = environment.constants?.language?.cultureMap?.find((o) => o.key === locale);
    return item ? item.value : locale;
  }
}
